'use client'

import { swrFetcher, ApiFetchError } from '@/hooks/api-fetcher'
import type { ImportResult } from '@/hooks/use-credential-confirm-stream'

/**
 * Post-import brand binding, extracted from the oauth-callback modal-close
 * chain so it can run from the always-mounted Sync Widget bridge after the
 * user has navigated away from the callback page.
 *
 * Single-brand orgs only: the caller is responsible for gating (the early
 * dialog collapse is only enabled when the org has exactly one brand, so the
 * binding target is unambiguous). Multi-brand allocation keeps the existing
 * interactive dialog on the callback page.
 */

export type PostImportBindingOutcome =
  | { status: 'bound'; accountCount: number }
  | { status: 'skipped' }
  | { status: 'failed'; error: string }

export async function applyPostImportBinding(args: {
  credentialId: number
  orgId: string
  brandId: string
  result: ImportResult
}): Promise<PostImportBindingOutcome> {
  const accounts = args.result.newlyImportedAccounts
  if (args.result.is_linking_only || accounts.length === 0) {
    return { status: 'skipped' }
  }

  try {
    // Same endpoint the callback page's saveAllocationPatches targets; the
    // bearer token is resolved inside swrFetcher from the token-manager
    // singleton, so this is safe to call outside any React context.
    await swrFetcher(
      `/api/v1/orgs/${args.orgId}/integrations/installs/${args.credentialId}/allocations`,
      {
        method: 'PATCH',
        body: {
          resources: accounts.map(account => ({
            resource_type: account.resource_type ?? 'ad_account',
            resource_id: account.id,
            brand_ids: [args.brandId],
          })),
        },
      },
    )
    return { status: 'bound', accountCount: accounts.length }
  } catch (error) {
    const message =
      error instanceof ApiFetchError
        ? error.message
        : error instanceof Error
          ? error.message
          : 'Allocation request failed'
    return { status: 'failed', error: message }
  }
}
